import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, FolderOpen, AlertCircle } from "lucide-react";
import { projectKeys } from "hooks/useSidebarProjects";
import { bugsnagClient } from "../../bugsnag";
import type { NotifiableError } from "@bugsnag/core/types/common";
import ProjectCard from "../ProjectCard";
import { InfiniteScrollContainer } from "../common/InfiniteScrollContainer";
import { TabsRoot, TabsList, TabsTrigger } from "components/common/Tabs";
import { DashboardPageHeader } from "./DashboardPageHeader";
import { ProjectScopeToggle } from "./ProjectScopeToggle";
import { useDashboardProjects } from "./useDashboardProjects";
import { ANALYTICS_EVENTS, trackEvent } from "../../utils/analytics";
import api from "../../utils/api";
import { useAuth } from "../../contexts/AuthContext";
import { useProjectScope } from "hooks/useProjectScope";
import { Action, projectSubject, Can } from "common/access";
import type { DashboardView, EditProjectInput } from "./types";

export const DashboardPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const canScopeToOrganization = Boolean(
    user?.isGlobalAdmin && user?.organizationId,
  );

  const [view, setView] = useState<DashboardView>("active");

  const { scope, setScope, organizationFilter } = useProjectScope({
    canScopeToOrganization,
    organizationId: user?.organizationId,
  });

  const {
    projects,
    isLoading,
    error,
    onLoadMore,
    canLoadMore,
    removeProject,
    updateProject,
  } = useDashboardProjects({ view, organization: organizationFilter });

  const newProjectSubject = useMemo(
    () => projectSubject({ organizationId: user?.organizationId }),
    [user?.organizationId],
  );

  const handleArchiveProject = async (projectId: string) => {
    try {
      await api.archiveProject(projectId);
      trackEvent(ANALYTICS_EVENTS.PROJECT_ARCHIVED, { projectId });
      removeProject(projectId);
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
    } catch (err) {
      console.error("Error archiving project:", err);
      bugsnagClient.notify(err as NotifiableError);
    }
  };

  const handleUnarchiveProject = async (projectId: string) => {
    try {
      await api.unarchiveProject(projectId);
      trackEvent(ANALYTICS_EVENTS.PROJECT_UNARCHIVED, { projectId });
      removeProject(projectId);
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
    } catch (err) {
      console.error("Error unarchiving project:", err);
      bugsnagClient.notify(err as NotifiableError);
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      await api.deleteProject(projectId);
      trackEvent(ANALYTICS_EVENTS.PROJECT_DELETED, { projectId });
      removeProject(projectId);
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
    } catch (err) {
      console.error("Error deleting project:", err);
      bugsnagClient.notify(err as NotifiableError);
    }
  };

  const handleEditProject = async (
    projectId: string,
    { name, description, teamChanges }: EditProjectInput,
  ) => {
    try {
      await api.updateProject(projectId, { name, description, teamChanges });
      trackEvent(ANALYTICS_EVENTS.PROJECT_UPDATED, {
        projectId,
        projectTitle: name,
      });
      updateProject(projectId, { title: name, description });
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
    } catch (err) {
      console.error("Error updating project:", err);
      bugsnagClient.notify(err as NotifiableError);
      throw err;
    }
  };

  return (
    <div className="space-y-6">
      <DashboardPageHeader />
      <TabsRoot<DashboardView>
        value={view}
        onValueChange={setView}
        styles={{ variant: "underline" }}
      >
        <TabsList>
          <TabsTrigger value="active">{t("dashboard.view.active")}</TabsTrigger>
          <TabsTrigger value="archived">
            {t("dashboard.view.archived")}
          </TabsTrigger>
        </TabsList>
      </TabsRoot>
      {canScopeToOrganization && (
        <div className="flex justify-start">
          <ProjectScopeToggle scope={scope} onScopeChange={setScope} />
        </div>
      )}
      {projects.length === 0 && isLoading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-gray-200 border-t-blue-600" />
          <p className="mt-3 text-sm text-gray-500">{t("loading_projects")}</p>
        </div>
      ) : error ? (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <AlertCircle
            size={20}
            className="text-red-500 mt-0.5 flex-shrink-0"
          />
          <div>
            <p className="text-sm font-medium text-red-800">
              {t("dashboard.error")}
            </p>
          </div>
        </div>
      ) : projects.length > 0 ? (
        <InfiniteScrollContainer
          onEndReached={onLoadMore}
          isActive={canLoadMore}
          isLoading={isLoading}
          rootMargin="200px"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onDelete={handleDeleteProject}
                onEdit={handleEditProject}
                onArchive={handleArchiveProject}
                onUnarchive={handleUnarchiveProject}
              />
            ))}
          </div>
        </InfiniteScrollContainer>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 py-16 px-6">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
            <FolderOpen size={24} className="text-gray-400" />
          </div>
          <h3 className="mt-4 text-sm font-semibold text-gray-900">
            {view === "archived"
              ? t("dashboard.no_archived_projects_title")
              : t("dashboard.no_projects_title")}
          </h3>
          <p className="mt-1 text-sm text-gray-500 text-center max-w-sm">
            {view === "archived"
              ? t("dashboard.no_archived_projects_message")
              : t("dashboard.no_projects_message")}
          </p>
          {view === "active" && (
            <Can I={Action.Create} this={newProjectSubject}>
              <button
                onClick={() => (window.location.href = "/project/new")}
                className="mt-5 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <Plus size={16} />
                <span>{t("dashboard.create_new_project")}</span>
              </button>
            </Can>
          )}
        </div>
      )}
    </div>
  );
};
